import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * Right-side size chart drawer. Uses the native dialog top layer so it stacks
 * above the side cart without closing it.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} [closeButton]
 *
 * @extends {Component<Refs>}
 */
class SizeChartDrawerComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {boolean} */
  #boundTriggers = false;

  /** @type {boolean} */
  #ownsScrollLock = false;

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('click', this.#onDialogClick);

    if (!this.#boundTriggers) {
      document.addEventListener('click', this.#onTriggerClick);
      this.#boundTriggers = true;
    }

    this.#setupTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;
    dialog.removeEventListener('cancel', this.#onCancel);
    dialog.removeEventListener('click', this.#onDialogClick);
    document.removeEventListener('click', this.#onTriggerClick);
    this.#boundTriggers = false;
    this.#releaseScrollLock();
  }

  open = () => {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#lockScrollIfNeeded();
    dialog.showModal();
    this.dispatchEvent(new CustomEvent('size-chart-drawer:open', { bubbles: true }));
  };

  close = () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.close();
    this.#releaseScrollLock();
    this.dispatchEvent(new CustomEvent('size-chart-drawer:close', { bubbles: true }));
  };

  #lockScrollIfNeeded() {
    if (document.body.style.position === 'fixed') {
      this.#ownsScrollLock = false;
      return;
    }

    this.#previousScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;
    this.#ownsScrollLock = true;
  }

  #releaseScrollLock() {
    if (!this.#ownsScrollLock) return;

    const anotherOverlayOpen = Boolean(
      document.querySelector(
        'cart-drawer-component dialog[open], dialog.cart-picker-modal--centered[open], dialog.dialog-modal[open]:not(.size-chart-drawer__dialog)'
      )
    );

    if (!anotherOverlayOpen) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    this.#ownsScrollLock = false;
  }

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.close();
    }
  };

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-open-size-chart]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.open();
  };

  #setupTabs() {
    const chart = this.querySelector('[data-size-chart]');
    if (!(chart instanceof HTMLElement)) return;

    chart.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const tab = target.closest('.size-chart__tab');
      if (!(tab instanceof HTMLElement) || !chart.contains(tab)) return;

      event.preventDefault();
      this.#activatePanel(chart, tab.dataset.panel);
    });
  }

  /**
   * @param {HTMLElement} chart
   * @param {string | undefined} name
   */
  #activatePanel(chart, name) {
    if (!name) return;

    chart.querySelectorAll('.size-chart__tab').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const active = tab.dataset.panel === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    chart.querySelectorAll('.size-chart__panel').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.classList.toggle('is-active', panel.dataset.panel === name);
    });
  }
}

if (!customElements.get('size-chart-drawer-component')) {
  customElements.define('size-chart-drawer-component', SizeChartDrawerComponent);
}
